import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle, Star, Zap, Clock, Shield, Phone, ArrowRight, Globe, Search, Smartphone, HeadphonesIcon, Lock, BarChart3, Check } from "lucide-react";
import { gtmClick } from "@/lib/gtm";
import { SEOHead, localBusinessSchema, websiteSchema } from "../components/SEOHead";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BENEFITS = [
  { icon: Clock, label: "Live in 48 Hours", desc: "Your website goes live within 48 hours. Guaranteed.", bg: "bg-[#FFD600]", span: "col-span-1 row-span-1" },
  { icon: Search, label: "SEO Optimised", desc: "Built to rank on Google from day one. More traffic, more customers.", bg: "bg-white", span: "col-span-1 row-span-1" },
  { icon: Smartphone, label: "Mobile Responsive", desc: "Looks perfect on every device. 60%+ of visitors are on mobile.", bg: "bg-[#0055FF] text-white", span: "col-span-1 row-span-1" },
  { icon: HeadphonesIcon, label: "100% Aussie Support", desc: "Real humans in Melbourne. Call us on 0466 476 264 any time.", bg: "bg-black text-white", span: "col-span-1 md:col-span-2 row-span-1" },
  { icon: Lock, label: "SSL Certificate", desc: "Secure & trusted by Google.", bg: "bg-white", span: "col-span-1 row-span-1" },
  { icon: BarChart3, label: "Google Analytics Setup", desc: "Track every visitor & conversion.", bg: "bg-[#FFD600]", span: "col-span-1 row-span-1" },
  { icon: Globe, label: ".com.au Domain — Just $10", desc: "Get your professional Australian domain for only $10. That's as cheap as it gets.", bg: "bg-white", span: "col-span-1 row-span-1" },
];

const STEPS = [
  { n: "01", title: "Book a Free Call", desc: "Chat with our Melbourne team about your business and goals." },
  { n: "02", title: "Share Your Details", desc: "Send us your logo, photos, and info — we handle the rest." },
  { n: "03", title: "We Build It", desc: "Our team builds your beautiful, fast website in 48 hours." },
  { n: "04", title: "Launch & Thrive", desc: "Go live, get found on Google, and start winning customers." },
];

export default function HomePage() {
  const [packages, setPackages] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/packages`).then(r => setPackages(r.data)),
      axios.get(`${API}/testimonials`).then(r => setTestimonials(r.data.slice(0, 3))),
      axios.get(`${API}/portfolio`).then(r => setPortfolio(r.data.slice(0, 3))),
      axios.get(`${API}/blog`).then(r => setPosts(r.data.slice(0, 3))),
    ]).catch(() => {});
  }, []);

  const cheapest = packages.length > 0 ? Math.min(...packages.map(p => p.price)) : 499;

  return (
    <main className="pt-16" data-testid="home-page">
      <SEOHead
        title="Professional Websites from $499"
        description="No Fuss Websites builds fast, SEO-ready, mobile-optimised websites for Australian businesses. Professional web design from just $499. Live in 48 hours. Based in Melbourne."
        path="/"
        schema={[localBusinessSchema(), websiteSchema()]}
      />
      {/* Hero */}
      <section className="bg-[#FDFDFD] section-padding relative overflow-hidden" data-testid="hero-section">
        <div className="container-main text-center relative z-10">
          <div className="inline-flex items-center gap-2 bg-[#FFD600] border-2 border-black px-4 py-2 rounded-full text-sm font-bold mb-8 animate-pulse-ring" data-testid="hero-badge">
            <Zap size={14} fill="black" />
            Websites Live in 48 Hours — Guaranteed
          </div>
          <h1 className="text-5xl md:text-7xl font-black tracking-tight leading-none mb-6 animate-fade-in-up" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="hero-headline">
            Professional Websites.<br />
            <span className="text-[#0055FF]">No Fuss.</span><br />
            Just ${cheapest}.
          </h1>
          <p className="text-xl md:text-2xl text-gray-700 font-medium mb-10 max-w-2xl mx-auto animate-fade-in-up animate-delay-200" data-testid="hero-subheadline">
            Fast, SEO-ready, mobile websites for Aussie businesses. Stop paying thousands — launch yours today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12 animate-fade-in-up animate-delay-300">
            <Link to="/purchase" className="btn-primary text-lg" data-testid="hero-cta-quote" onClick={() => gtmClick("hero_get_started")}>
              Get Started — From ${cheapest}
            </Link>
            <span className="text-gray-500 font-bold text-sm">or</span>
            <Link to="/quote" className="btn-secondary text-lg" data-testid="hero-cta-free-quote" onClick={() => gtmClick("hero_free_quote")}>
              Get a Free Quote
            </Link>
          </div>
          {/* Social proof */}
          <div className="flex flex-wrap items-center justify-center gap-8 text-sm font-semibold" data-testid="hero-social-proof">
            <div className="flex items-center gap-2">
              <div className="flex">
                {[1,2,3,4,5].map(i => <Star key={i} size={16} className="text-[#FFD600] fill-[#FFD600]" />)}
              </div>
              <span>4.9/5 Rating</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle size={16} className="text-green-600" />
              <span>200+ Happy Clients</span>
            </div>
            <div className="flex items-center gap-2">
              <Shield size={16} className="text-[#0055FF]" />
              <span>100% Aussie Owned</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock size={16} className="text-black" />
              <span>Live in 48 Hours</span>
            </div>
          </div>
        </div>
        {/* BG decoration */}
        <div className="absolute -top-20 -right-20 w-96 h-96 bg-[#FFD600] rounded-full opacity-10 pointer-events-none" />
        <div className="absolute -bottom-20 -left-20 w-80 h-80 bg-[#0055FF] rounded-full opacity-5 pointer-events-none" />
      </section>

      {/* Ticker */}
      <div className="bg-black text-[#FFD600] border-y-4 border-[#FFD600] py-4 overflow-hidden" data-testid="ticker">
        <div className="ticker-content">
          {[...Array(4)].map((_, k) => (
            <span key={k} className="flex gap-12 mr-12">
              {["Live in 48 Hours", "SEO Optimised", "Mobile Responsive", "Hosting $99/yr", "Aussie Support", `Starting at $${cheapest}`, "Domain $10/yr"].map((t, i) => (
                <span key={i} className="font-black text-sm uppercase tracking-widest flex items-center gap-3 px-6">
                  <Zap size={12} fill="#FFD600" /> {t}
                </span>
              ))}
            </span>
          ))}
        </div>
      </div>

      {/* Benefits Bento Grid */}
      <section className="section-padding bg-[#F4F4F5]" data-testid="benefits-section">
        <div className="container-main">
          <div className="text-center mb-14">
            <p className="text-sm font-bold uppercase tracking-widest text-[#0055FF] mb-3">Why Choose Us</p>
            <h2 className="text-4xl md:text-5xl font-black" style={{ fontFamily: "Outfit, sans-serif" }}>
              Everything You Need.<br />Nothing You Don't.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {BENEFITS.map((b, i) => (
              <div
                key={i}
                className={`${b.bg} border-2 border-black rounded-2xl p-8 transition-transform duration-150 hover:-translate-y-1 ${b.span}`}
                style={{ boxShadow: "4px 4px 0px 0px rgba(0,0,0,1)" }}
                data-testid={`benefit-card-${i}`}
              >
                <b.icon size={32} className="mb-4" />
                <h3 className="text-xl font-black mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>{b.label}</h3>
                <p className="text-sm opacity-80 leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Packages Preview */}
      <section className="section-padding" data-testid="packages-preview-section">
        <div className="container-main">
          <div className="text-center mb-14">
            <p className="text-sm font-bold uppercase tracking-widest text-[#0055FF] mb-3">Transparent Pricing</p>
            <h2 className="text-4xl md:text-5xl font-black" style={{ fontFamily: "Outfit, sans-serif" }}>
              Simple. Honest. Affordable.
            </h2>
            <p className="text-lg text-gray-600 mt-4">One-time price. No hidden fees. No monthly surprises.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {packages.map((pkg) => (
              <div
                key={pkg.id}
                className={`border-2 border-black rounded-2xl p-8 flex flex-col transition-transform hover:-translate-y-1 duration-150 ${
                  pkg.is_popular ? "bg-[#FFD600]" : "bg-white"
                }`}
                style={{ boxShadow: "4px 4px 0px 0px rgba(0,0,0,1)" }}
                data-testid={`package-card-${pkg.name?.toLowerCase()}`}
              >
                {pkg.is_popular && (
                  <span className="bg-black text-[#FFD600] text-xs font-black px-3 py-1 rounded-full w-fit mb-4 uppercase tracking-wider">
                    Most Popular
                  </span>
                )}
                {pkg.original_price && pkg.original_price > pkg.price && (
                  <span className="bg-red-500 text-white text-xs font-black px-3 py-1 rounded-full w-fit mb-4 uppercase tracking-wider">
                    {Math.round(((pkg.original_price - pkg.price) / pkg.original_price) * 100)}% OFF
                  </span>
                )}
                <h3 className="text-2xl font-black mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>{pkg.name}</h3>
                <p className="text-sm text-gray-600 mb-6">{pkg.tagline}</p>
                <div className="flex items-end gap-1 mb-8">
                  {pkg.original_price && pkg.original_price > pkg.price && (
                    <span className="text-xl font-bold line-through text-gray-400 mb-1.5">${pkg.original_price}</span>
                  )}
                  <span className="text-5xl font-black" style={{ fontFamily: "Outfit, sans-serif" }}>${pkg.price}</span>
                  <span className="text-sm font-semibold text-gray-600 mb-2">{pkg.period}</span>
                </div>
                <ul className="space-y-2 mb-8 flex-1">
                  {(pkg.features || []).slice(0, 6).map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <Check size={14} className="mt-0.5 shrink-0 text-green-600" />
                      <span>{f}</span>
                    </li>
                  ))}
                  {pkg.features?.length > 6 && (
                    <li className="text-sm font-semibold text-[#0055FF]">+{pkg.features.length - 6} more features...</li>
                  )}
                </ul>
                <Link
                  to={`/purchase?package=${pkg.id}`}
                  className={`text-center font-bold py-3 rounded-full border-2 border-black transition-all hover:-translate-y-0.5 ${
                    pkg.is_popular ? "bg-black text-white" : "bg-[#FFD600] text-black"
                  }`}
                  style={{ boxShadow: "3px 3px 0px 0px rgba(0,0,0,1)" }}
                  data-testid={`package-cta-${pkg.name?.toLowerCase()}`}
                  onClick={() => gtmClick(`package_${pkg.name?.toLowerCase()}`, "package_select")}
                >
                  Get Started
                </Link>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/packages" className="btn-secondary inline-flex items-center gap-2" data-testid="view-all-packages-btn">
              View Full Package Details <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section-padding bg-black text-white" data-testid="how-it-works-section">
        <div className="container-main">
          <div className="text-center mb-14">
            <p className="text-sm font-bold uppercase tracking-widest text-[#FFD600] mb-3">The Process</p>
            <h2 className="text-4xl md:text-5xl font-black text-white" style={{ fontFamily: "Outfit, sans-serif" }}>
              Simple as 1-2-3-4
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {STEPS.map((step, i) => (
              <div key={i} className="relative" data-testid={`step-${i+1}`}>
                <div className="bg-white text-black border-2 border-white rounded-2xl p-8 h-full" style={{ boxShadow: "4px 4px 0px 0px #FFD600" }}>
                  <div className="text-4xl font-black text-[#FFD600] mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>{step.n}</div>
                  <h3 className="text-xl font-black mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>{step.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{step.desc}</p>
                </div>
                {i < STEPS.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-3 z-10 text-[#FFD600] text-2xl font-black">→</div>
                )}
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/purchase" className="btn-primary text-lg inline-flex items-center gap-2" data-testid="hiw-cta" onClick={() => gtmClick("how_it_works_start")}>
              Start Your Website Today <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      {testimonials.length > 0 && (
        <section className="section-padding bg-[#F4F4F5]" data-testid="testimonials-section">
          <div className="container-main">
            <div className="text-center mb-14">
              <p className="text-sm font-bold uppercase tracking-widest text-[#0055FF] mb-3">Real Stories</p>
              <h2 className="text-4xl md:text-5xl font-black" style={{ fontFamily: "Outfit, sans-serif" }}>
                What Aussie Businesses Say
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {testimonials.map((t, i) => (
                <div key={t.id} className="card-brutal flex flex-col" data-testid={`testimonial-${i}`}>
                  <div className="flex mb-4">
                    {[...Array(t.rating)].map((_, j) => <Star key={j} size={16} className="text-[#FFD600] fill-[#FFD600]" />)}
                  </div>
                  <p className="text-gray-700 leading-relaxed mb-6 flex-1 italic">"{t.review}"</p>
                  <div className="flex items-center gap-3 pt-4 border-t-2 border-black">
                    {t.avatar_url ? (
                      <img src={t.avatar_url} alt={t.client_name} className="w-10 h-10 rounded-full border-2 border-black object-cover" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-[#FFD600] border-2 border-black flex items-center justify-center font-black text-sm">
                        {t.client_name[0]}
                      </div>
                    )}
                    <div>
                      <p className="font-black text-sm">{t.client_name}</p>
                      <p className="text-xs text-gray-500">{t.business_name}</p>
                    </div>
                    {t.package_used && (
                      <span className="ml-auto text-xs font-bold bg-[#FFD600] border border-black px-2 py-0.5 rounded-full">{t.package_used}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Portfolio Preview */}
      {portfolio.length > 0 && (
        <section className="section-padding" data-testid="portfolio-preview-section">
          <div className="container-main">
            <div className="text-center mb-14">
              <p className="text-sm font-bold uppercase tracking-widest text-[#0055FF] mb-3">Our Work</p>
              <h2 className="text-4xl md:text-5xl font-black" style={{ fontFamily: "Outfit, sans-serif" }}>
                Real Websites. Real Results.
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {portfolio.map((item, i) => (
                <div key={item.id} className="group border-2 border-black rounded-2xl overflow-hidden transition-transform hover:-translate-y-1 bg-white" style={{ boxShadow: "4px 4px 0px 0px rgba(0,0,0,1)" }} data-testid={`portfolio-item-${i}`}>
                  <div className="relative h-48 overflow-hidden">
                    <img src={item.image_url} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute top-3 left-3">
                      <span className="bg-[#FFD600] border-2 border-black text-xs font-black px-3 py-1 rounded-full">{item.category}</span>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-lg font-black mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>{item.title}</h3>
                    <p className="text-sm text-gray-600">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="text-center mt-10">
              <Link to="/portfolio" className="btn-secondary inline-flex items-center gap-2" data-testid="view-portfolio-btn">
                View All Projects <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Blog Preview - removed */}

      {/* SEO Internal Links */}
      <section className="section-padding bg-white border-t-2 border-black">
        <div className="container-main">
          <h2 className="text-2xl font-black mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>Quick Links</h2>
          <p className="text-gray-600 mb-6">Everything you need to get started with your new website.</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { title: "Packages & Pricing", link: "/packages" },
              { title: "How It Works", link: "/how-it-works" },
              { title: "Portfolio", link: "/portfolio" },
              { title: "FAQ", link: "/faq" },
              { title: "Get a Free Quote", link: "/quote" },
              { title: "About Us", link: "/about" },
              { title: "Blog", link: "/blog" },
              { title: "Contact Us", link: "/contact" },
            ].map((item, i) => (
              <Link key={i} to={item.link} className="border-2 border-black rounded-lg p-3 font-bold text-sm hover:-translate-y-0.5 hover:bg-[#FFD600] transition-all text-center">
                {item.title}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
